import { APP_INITIALIZER, ApplicationConfig, Injector, LOCALE_ID, PLATFORM_ID, inject, provideZoneChangeDetection } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { provideRouter, withPreloading, PreloadAllModules, withRouterConfig, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { testModeInterceptor } from './core/interceptors/test-mode.interceptor';
import { hubBaseUrlInterceptor } from './core/interceptors/hub-base-url.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { edgeClientDeviceInterceptor } from './core/interceptors/edge-client-device.interceptor';
import { hubQueuedWarningInterceptor } from './core/interceptors/hub-queued-warning.interceptor';
import { pinCheckPendingInterceptor } from './core/interceptors/pin-check-pending.interceptor';
import { DeviceIdentityService } from './core/services/device-identity.service';
import { EdgeBridgeService } from './core/edge/edge-bridge.service';
import { watchIntegratedSaleOutcomes } from './features/pos/settle-screen/settle-integrated-sale';
import { NetworkStatusService } from './core/offline/network-status.service';
import { HUB_DISCOVERY_INITIALIZER } from './core/offline/hub-bootstrap.service';
import { hubCanTakeOverHere } from './core/offline/hub-surfaces';
import { ReconnectPolicyService } from './core/offline/reconnect-policy.service';
import { provideTranslocoRoot } from './transloco-root';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      // After bootstrap, preload all lazy route chunks in the background.
      // The service worker caches each one as it's downloaded, so a
      // subsequent offline refresh can serve every /pos route chunk from
      // the SW cache. Critical for POS terminals.
      withPreloading(PreloadAllModules),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' })
    ),
    provideHttpClient(withInterceptors([
      pinCheckPendingInterceptor,   // FAST-PIN — retry a write the hub held for its PIN check (outermost: a retry re-runs the chain).
      hubBaseUrlInterceptor,        // Rewrite base URL to the Local Hub Agent when WAN is down.
      hubQueuedWarningInterceptor,  // OFP-009 — warn when the hub queued a write it cannot replay.
      authInterceptor,              // Bearer token + tenant/store headers.
      testModeInterceptor,          // X-Test-Mode on /api/pos/* when Test Mode is on.
      edgeClientDeviceInterceptor,  // X-Edge-Client-Device-Id (hardware id or 'web').
      errorInterceptor,             // Global 401 / 402 handling.
    ])),
    provideAnimationsAsync(),
    provideTranslocoRoot(),

    // Synchronously read the persisted UI language from localStorage and map
    // it to a CLDR locale. Falls back to 'en-GB' on first-ever load before
    // LanguageService has hydrated. LanguageService triggers a full reload
    // on switch so LOCALE_ID is re-read on next bootstrap — Angular does not
    // support live LOCALE_ID swap.
    {
      provide: LOCALE_ID,
      useFactory: () => {
        try {
          const lang = (typeof localStorage !== 'undefined'
            ? localStorage.getItem('ipos.lang')
            : null);
          return lang === 'ar' ? 'ar-AE' : 'en-GB';
        } catch {
          return 'en-GB';
        }
      },
    },

    // ── Network status heartbeat (hub-only offline) ───────────────
    // Browser-only. The hub-base-url interceptor reads net.current() on
    // each request to decide cloud vs. hub routing. Fire-and-forget so a
    // network probe never blocks bootstrap.
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => {
        const platformId = inject(PLATFORM_ID);
        const injector = inject(Injector);
        return () => {
          if (!isPlatformBrowser(platformId)) return Promise.resolve();
          const net = injector.get(NetworkStatusService);
          // RECON-18 — where no hub can take over, recovery from an outage
          // needs two good probes, not RECON-1's 60 s. Read lazily on each
          // probe, so the route and store at that moment decide.
          net.setHubCanTakeOver(() => hubCanTakeOverHere(injector));
          // OF-SWITCH-FAST — where the cashier confirms Go Online, recovery
          // only offers the badge, so it needs 15 s of proof, not 60 s.
          net.setManualGoOnline(
            () => injector.get(ReconnectPolicyService).confirmsBothWays(),
          );
          net.startHeartbeat();
          return Promise.resolve();
        };
      },
    },

    // ── Hub discovery (OFP-082) ────────────────────────────────────
    // Starts probing the store hub from the store cached in local storage
    // (or, before its settings ever loaded, the hub the edge shell reports),
    // so a reload on a screen the POS shell does not mount — order, settle —
    // still finds the hub. Browser-only, POS surface only, never blocks.
    HUB_DISCOVERY_INITIALIZER,

    // ── Edge bridge eager-instantiation ─────────────────────────────
    // EdgeBridgeService is providedIn:'root' (lazy). Force construct at
    // boot so it: (a) installs window.__edgeBridge.__deliver before any
    // Dart-side `emit` lands, (b) fires `bridge:ready` to flush the
    // pre-boot buffer on the Dart side, (c) exposes itself as
    // `window.__edge` for testing from WebView dev tools.
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => {
        const platformId = inject(PLATFORM_ID);
        const injector = inject(Injector);
        return () => {
          if (isPlatformBrowser(platformId)) {
            // PAY-3 — a card sale's late outcome is kept on this device
            // whatever screen is open (see captureIntegratedSaleOutcome).
            watchIntegratedSaleOutcomes(injector.get(EdgeBridgeService));
          }
          return Promise.resolve();
        };
      },
    },

    // ── Durable device identity ─────────────────────────────────────
    // Resolve the hardware Edge Client id (via device:info bridge, falling
    // back to localStorage / legacy sessionStorage) and cache it
    // synchronously so edgeClientDeviceInterceptor can attach the header
    // on the very first request. Never throws — a failure leaves the
    // 'web' sentinel in effect.
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: (identity: DeviceIdentityService) => () => identity.init(),
      deps: [DeviceIdentityService],
    },
  ],
};
